<template>
    <v-container class="pa-4">
        <v-card max-width="700" elevation="0">
            <v-row no-gutters>
                <v-col cols="4">
                    <v-img
                        :src="villageImage"
                        aspect-ratio="1.5"
                        cover
                    ></v-img>
                </v-col>
                <v-col cols="8" class="d-flex flex-column justify-center pa-4">
                    <v-card-title class="text-h6"> World Items </v-card-title>
                    <v-card-text>
                        All items instantiated in the world.
                    </v-card-text>
                </v-col>
            </v-row>
        </v-card>
        <v-row class="mb-2 justify-end align-center" no-gutters>
            <v-col cols="auto" class="me-2">
                <v-select
                    v-model="selectedServer"
                    :items="servers"
                    item-title="name"
                    item-value="serverGuid"
                    label="Server"
                    hide-details
                    density="compact"
                    style="min-width: 200px"
                    @update:modelValue="reloadMaps"
                ></v-select>
            </v-col>
            <v-col cols="auto" class="me-2">
                <v-select
                    v-model="selectedMap"
                    :items="maps"
                    item-title="name"
                    item-value="mapGuid"
                    label="Map"
                    density="compact"
                    hide-details
                    style="min-width: 200px"
                    @update:modelValue="reloadItems"
                ></v-select>
            </v-col>
            <v-col cols="auto">
                <v-btn color="primary" @click="createNewItem"> New Item </v-btn>
            </v-col>
        </v-row>
    </v-container>

    <v-data-table
        density="compact"
        :headers="headers"
        :items="items"
        item-value="id"
        @click:row="handleRowClick"
        :items-per-page="25"
        :items-per-page-options="[10, 25, 50, 100]"
    >
        <template #[`item.properties`]="{ item }">
            <div style="max-width: 1250px; white-space: normal; display: flex; flex-wrap: wrap; gap: 4px;">
                <v-chip
                    v-for="(property, index) in item.properties"
                    :key="index"
                    class="ma-1"
                    color="primary"
                    label
                    small
                >
                    {{ property.name }}:{{  formatProperty(property.value) }}
                </v-chip>
            </div>
        </template>

        <template #[`item.containerData`]="{ item }">
                <v-chip
                    v-if="getItemsCount(item.containerData) !== null"
                    class="ma-1"
                    color="primary"
                    label
                    small
                >
                    {{  getItemsCount(item.containerData) }}
                </v-chip>
        </template>

        <template #[`item.Transform`]="{ item }">
            <div class="cell-with-copy" @mouseenter="hoveredCell = item.Transform" @mouseleave="hoveredCell = null">
                {{ item.Transform }}
                <v-icon 
                    class="copy-icon"
                    size="small"
                    :style="{ visibility: hoveredCell === item.Transform ? 'visible' : 'hidden' }"
                    @click.stop="copyToClipboard(item.Transform)"
                >
                mdi-content-copy
                </v-icon>
            </div>
        </template>

        <template #[`item.actions`]="{ item }">
            <v-icon size="small" class="me-2" @click="editItem(item)"
                >mdi-pencil</v-icon
            >
            <v-icon size="small" class="me-2" @click.stop="deleteItem(item)"
                >mdi-trash-can</v-icon
            >
        </template>
    </v-data-table>
    
    <edit-world-item 
        v-model="dialogEdit" 
        :item="editingItem" 
        :selected-server="selectedServer"
        :selected-map="selectedMap"
        @request-reload-worlditems="reloadItems"
        />

    <v-dialog v-model="dialogDelete" max-width="400" scrollable>
        <v-card>
            <v-card-title class="text-h6">Are you sure?</v-card-title>
            <v-card-text>
                <div style="max-height: 60vh; overflow-y: auto;">
                    This action cannot be undone.
                </div>
            </v-card-text>
            <v-card-actions class="justify-end">
                <v-btn text @click="cancelDelete">Cancel</v-btn>
                <v-btn color="error" text @click="confirmDelete"
                    >Yes, Delete</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>

</template>

<script>
import EditWorldItem from './EditWorldItem.vue';
import villageImage from '@/assets/village.png';
import { ref } from 'vue'

export default {
    data() {
        return {
            isBusy: false,
            villageImage,
            servers: [],
            maps: [],
            items: [],
            selectedServer: null,
            selectedMap: null,
            hoveredCell: ref(null),
            editingItem: null,
            deletingItem: null,
            dialogEdit: false,
            dialogDelete: false,
            headers: [
                { title: 'Handle', key: 'itemHandle' },
                { title: 'Owner', key: 'ownerGuid' },
                { title: 'Spawner', key: 'spawnerGuid' },
                { title: 'Location', key: 'Transform' },
                { title: 'Properties', key: 'properties' },
                { title: 'Container', key: 'containerData' },
                { title: 'Created', key: 'createdAt' },
                { title: 'Updated', key: 'updatedAt' },
                { title: 'Actions', key: 'actions', sortable: false },
            ],
        };
    },
    components: {
        EditWorldItem,
    },
    mounted() {
        this.reloadServers();
    },
    methods: {
        reloadServers() {  // page was loaded
            console.log('Retrieving servers...');
            const authToken = localStorage.getItem('Auth');

            if (authToken) {
                fetch(`${process.env.VUE_APP_STONECREEK_BACKEND}/api/v2/servers`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${authToken}`,
                    },
                })
                .then((response) => {
                    if (!response.ok) throw new Error('Fetch failed');

                    return response.json();
                })
                .then((json) => {
                    this.servers = json.servers.map((server) => ({
                        ...server,
                    }));

                    const selectedServer = localStorage.getItem('WorldItems.SelectedServer');
                    if (selectedServer) {
                        console.log("Server previously selected:" + selectedServer);
                        this.selectedServer = selectedServer;
                        this.reloadMaps();
                    }
                    else
                        this.selectedMap = null;
                })
                .catch((error) => {
                    console.error('Failed to load servers:', error);
                });
            }
        },
        copyToClipboard(text) {
            navigator.clipboard.writeText(text)
            .then(() => {
                console.log('Copied:', text)
                })
            .catch(err => 
                {
                    console.error('Copy failed:', err)
                })
        },
        reloadMaps() { // server was selected

            console.log('Retrieving maps...');
            const authToken = localStorage.getItem('Auth');
            localStorage.setItem('WorldItems.SelectedServer', this.selectedServer);

            if (authToken) {
                fetch(`${process.env.VUE_APP_STONECREEK_BACKEND}/api/v2/maps/${this.selectedServer}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${authToken}`,
                    },
                })
                .then((response) => {
                    if (!response.ok) throw new Error('Fetch failed');

                    return response.json();
                })
                .then((json) => {
                    this.maps = json.maps;

                    const selectedMap = localStorage.getItem('WorldItems.SelectedMap');
                    if (selectedMap) {
                        console.log("Map previously selected:" + selectedMap);
                        this.selectedMap = selectedMap;
                        this.reloadItems();
                    }
                    else
                        this.selectedMap = null;
                })
                .catch((error) => {
                    console.error('Failed to load maps:', error);
                });
            }
        },
        reloadItems() {
            console.log(`Retrieving world items from ${this.selectedServer}:${this.selectedMap}...`);

            localStorage.setItem('WorldItems.SelectedMap', this.selectedMap);
            const authToken = localStorage.getItem('Auth');

            if (authToken) {
                fetch(`${process.env.VUE_APP_STONECREEK_BACKEND}/api/v2/worlditems/${this.selectedServer}/${this.selectedMap}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${authToken}`,
                    },
                })
                .then((response) => {
                    if (!response.ok) throw new Error('Fetch failed');

                    return response.json();
                })
                .then((json) => {
                    this.items = json.items.map((item) => ({
                        ...item,
                        Transform: this.formatTransform(item.coordX, item.coordY, item.coordZ, item.rotation),
                    }));
                })
                .catch((error) => {
                    console.error('Failed to load world items:', error);
                });
            }
        },
        confirmDelete() {
            const token = localStorage.getItem('Auth');
            this.isBusy = true;
            fetch(`${process.env.VUE_APP_STONECREEK_BACKEND}/api/v2/worlditems/${this.selectedServer}/${this.selectedMap}/${this.deletingItem.itemGuid}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((response) => {
                if (!response.ok) throw new Error('Fetch failed');

                this.isBusy = false;
                this.deletingItem = null;
                this.dialogDelete = false;

                this.reloadItems();
            })
            .catch((error) => {
                console.error('Failed to load items:', error);
            });
        },
        closeDialog() {
            this.editingItem = null;
            this.dialogEdit = false;
        },
        createNewItem() {
            this.editingItem = {};
            this.dialogEdit = true;
        },
        saveItem() {
            this.closeDialog();
        },
        handleRowClick(event, item) {
            this.editingItem = item.item;
            this.dialogEdit = true;
        },
        editItem(item) {
            this.editingItem = item;
            this.dialogEdit = true;
        },
        deleteItem(item) {
            this.deletingItem = item;
            this.dialogDelete = true;
        },
        cancelDelete() {
            this.deletingItem = null;
            this.dialogDelete = false;
        },
        formatProperty(value) {
            if (typeof value !== 'object' )
                return value;
            
            return "array of " + value.length;
        },
        formatTransform(x,y,z,r) {
            return `${x},${y},${z}`;
        },
        getItemsCount(containerData) {
            if (!containerData) return null;
            let data = containerData;
            if (typeof data === 'string') {
                try { data = JSON.parse(containerData); }
                catch { return null; }
            }

            const items = data?.items;
            return "items:" + (Array.isArray(items)? items.length : '');
        }
    },
};
</script>

<style scoped>
.cell-with-copy {
  position: relative;
  display: flex;
  align-items: center;
}

.copy-icon {
  margin-left: 8px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.copy-icon:hover {
  opacity: 1;
}
</style>
